import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { api, inr, inrPlain } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useGroup } from "@/context/GroupContext";
import { ArrowUpRight, ArrowDownRight, Receipt, PlusCircle, Users2, TrendingUp } from "lucide-react";
import { format } from "date-fns";
import SettlementDialog from "@/components/SettlementDialog";
import { CardSkeleton, ListSkeleton } from "@/components/Skeletons";

export default function Dashboard() {
  const { user } = useAuth();
  const { slug, group, groupId, members } = useGroup();
  const [data, setData] = useState(null);
  const [recentExpenses, setRecentExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settle, setSettle] = useState(null);
  const navigate = useNavigate();

  const users = React.useMemo(() => Object.fromEntries((members || []).map((u) => [u.id, u])), [members]);

  const load = async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      const [dash, exps] = await Promise.all([
        api.get(`/dashboard`, { params: { group_id: groupId } }),
        api.get(`/expenses`, { params: { group_id: groupId } }),
      ]);
      setData(dash.data);
      setRecentExpenses(exps.data.slice(0, 8));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const net = data?.net_balance ?? 0;
  const isPositive = net >= 0;

  if (!groupId) {
    return (
      <div className="py-10 text-center">
        <Users2 className="w-10 h-10 mx-auto text-slate-300" />
        <p className="mt-3 text-sm text-slate-500">Loading group…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 lg:max-w-6xl">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Hey {user?.name?.split(" ")[0]},</div>
          <h1 className="heading text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight mt-1">{group?.name}</h1>
        </div>
        <button
          onClick={() => navigate(`/${slug}/add-expense`)}
          className="hidden lg:flex items-center gap-2 px-5 h-11 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold active:scale-95"
          data-testid="dashboard-desktop-add"
        >
          <PlusCircle className="w-4 h-4" /> Add expense
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Hero balance card - spans 2 cols on desktop */}
        {loading || !data ? (
          <div className="lg:col-span-2"><CardSkeleton /></div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className={`lg:col-span-2 relative overflow-hidden rounded-3xl p-8 card-hero border ${
              isPositive ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"
            } grain`}
            data-testid="dashboard-hero-card"
          >
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Your net balance</div>
              <div className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                isPositive ? "bg-emerald-600 text-white" : "bg-rose-600 text-white"
              }`}>
                {isPositive ? "COLLECT" : "PAY"}
              </div>
            </div>
            <div className="mt-4 flex items-baseline gap-2">
              <div className={`amount text-5xl lg:text-6xl font-bold tracking-tight ${isPositive ? "text-emerald-700" : "text-rose-700"}`} data-testid="dashboard-net-balance">
                {inr(net)}
              </div>
            </div>
            <p className="mt-2 text-sm text-slate-600">
              {isPositive
                ? net === 0 ? "You are all settled up." : "You should collect this amount overall."
                : "You need to pay this amount overall."}
            </p>

            <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatMini label="Expenses" value={data.total_expenses} data-testid="dashboard-total-expenses" />
              <StatMini label="Total spent" value={inrPlain(data.total_amount)} />
              <StatMini label="People" value={(group?.member_ids?.length || 0)} />
              <StatMini label="Your share" value={inrPlain(data.total_amount / Math.max(1, group?.member_ids?.length || 1))} />
            </div>
          </motion.div>
        )}

        {/* Quick actions column */}
        <div className="grid grid-cols-2 lg:grid-cols-1 gap-3">
          <button
            onClick={() => navigate("/add-expense")}
            className="p-5 rounded-2xl bg-slate-900 text-white text-left active:scale-95 transition-transform h-full"
            data-testid="quick-add-expense"
          >
            <PlusCircle className="w-5 h-5" />
            <div className="mt-4 heading font-semibold">Add expense</div>
            <div className="text-xs text-slate-300 mt-1">Log a new split</div>
          </button>
          <button
            onClick={() => navigate("/settlements")}
            className="p-5 rounded-2xl bg-white border border-slate-200 text-left active:scale-95 transition-transform h-full"
            data-testid="quick-settlements"
          >
            <Receipt className="w-5 h-5 text-slate-700" />
            <div className="mt-4 heading font-semibold text-slate-900">Settlements</div>
            <div className="text-xs text-slate-500 mt-1">View history</div>
          </button>
        </div>
      </div>

      {/* People + Recent expenses (two columns on desktop) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="heading text-lg font-semibold text-slate-900">People</h2>
            <button onClick={() => navigate("/members")} className="text-xs text-slate-500 hover:text-slate-900 font-semibold" data-testid="dashboard-see-all-people">
              See all
            </button>
          </div>
          {loading ? (
            <ListSkeleton rows={3} />
          ) : (
            <div className="space-y-3">
              {data?.people.map(({ user: u, balance }, i) => {
                const pos = balance > 0.005;
                const neg = balance < -0.005;
                return (
                  <motion.div
                    key={u.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                    className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-slate-200 hover:border-slate-300 transition-colors"
                    data-testid={`people-row-${u.id}`}
                  >
                    <button
                      onClick={() => navigate(`/${slug}/members/${u.id}`)}
                      className="flex items-center gap-3 flex-1 text-left"
                    >
                      <div className="w-11 h-11 rounded-full overflow-hidden ring-1 ring-slate-200 bg-slate-100">
                        {u.photo ? <img src={u.photo} alt={u.name} className="w-full h-full object-cover" /> : (
                          <div className="w-full h-full grid place-items-center font-semibold text-slate-700">{u.name?.[0]}</div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-slate-900">{u.name}</div>
                        <div className={`text-xs mt-0.5 font-semibold flex items-center gap-1 ${pos ? "text-emerald-600" : neg ? "text-rose-600" : "text-slate-400"}`}>
                          {pos ? <ArrowDownRight className="w-3 h-3" /> : neg ? <ArrowUpRight className="w-3 h-3" /> : null}
                          {pos ? `owes you ${inrPlain(Math.abs(balance))}` : neg ? `you owe ${inrPlain(Math.abs(balance))}` : "settled up"}
                        </div>
                      </div>
                    </button>

                    {pos && (
                      <button
                        className="text-xs font-semibold px-3 py-2 rounded-full bg-slate-900 text-white active:scale-95"
                        onClick={() => navigate(`/${slug}/members/${u.id}`)}
                        data-testid={`remind-${u.id}`}
                      >
                        Remind
                      </button>
                    )}
                    {neg && (
                      <button
                        className="text-xs font-semibold px-3 py-2 rounded-full bg-emerald-600 text-white active:scale-95"
                        onClick={() => setSettle({ otherId: u.id, outstanding: Math.abs(balance) })}
                        data-testid={`settle-${u.id}`}
                      >
                        Settle
                      </button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}
        </section>

        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="heading text-lg font-semibold text-slate-900">Recent expenses</h2>
            <TrendingUp className="w-4 h-4 text-slate-400" />
          </div>
          {loading ? (
            <ListSkeleton rows={4} />
          ) : recentExpenses.length === 0 ? (
            <div className="text-sm text-slate-500 p-4 rounded-2xl bg-white border border-slate-200">
              No expenses yet. Add your first one.
            </div>
          ) : (
            <div className="space-y-2">
              {recentExpenses.map((e) => {
                const payer = users[e.paid_by];
                return (
                  <button
                    key={e.id}
                    onClick={() => navigate(`/${slug}/expenses/${e.id}`)}
                    className="w-full text-left flex items-center gap-3 p-3 rounded-2xl bg-white border border-slate-200 hover:border-slate-300 transition-colors"
                    data-testid={`recent-expense-${e.id}`}
                  >
                    {payer?.photo && (
                      <img src={payer.photo} alt="" className="w-9 h-9 rounded-full object-cover ring-1 ring-slate-200" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 text-sm truncate">{e.title}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {payer?.name} paid • {format(new Date(e.expense_date || e.created_at), "PP")}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="amount font-bold text-slate-900 text-sm">{inrPlain(e.amount)}</div>
                      <div className="text-[10px] text-slate-400">₹{e.share_amount} each</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {settle && (
        <SettlementDialog
          open={!!settle}
          onOpenChange={(o) => !o && setSettle(null)}
          groupId={groupId}
          payerId={user.id}
          receiverId={settle.otherId}
          outstanding={settle.outstanding}
          onSuccess={load}
        />
      )}
    </div>
  );
}

function StatMini({ label, value, ...rest }) {
  return (
    <div className="bg-white/70 backdrop-blur rounded-2xl px-3 py-2.5" {...rest}>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="amount text-lg font-bold text-slate-900">{value}</div>
    </div>
  );
}
